import React, { useCallback, useEffect, useRef, useState } from "react";
import fixWebmDuration from "webm-duration-fix";

// react-pintura
import { PinturaEditor } from "@pqina/react-pintura";

// pintura
import "@pqina/pintura/pintura.css";
import {
  createDefaultImageReader,
  createDefaultShapePreprocessor,
  findNode,
  getEditorDefaults,
  removeNode,
} from "@pqina/pintura";
import { getLanguage } from "../../../../locales/i18n";
import {
  setPlugins,
  createDefaultImageWriter,
  createDefaultMediaWriter,
  imageStateToCanvas,
} from "@pqina/pintura";
import "@pqina/pintura-video/pinturavideo.css";
import {
  plugin_trim_locale_en_gb,
  plugin_trim,
  createMuxerEncoder,
  createDefaultVideoWriter,
  createMediaStreamEncoder,
} from "@pqina/pintura-video";
import * as Mp4Muxer from "mp4-muxer";

import {
  EditorImageConfig,
  generateFirebaseUploadUrl,
  generateMD5Hash,
  generateTransparentPNG,
  getVideoThumbnail,
} from "../../../../redux/slices/lomavisCreator/utils";

import { Button, ProgressBar } from "react-bootstrap";
import { EDITOR_VIEW, UPLOAD_VIEW } from "../../config/constants";
import { useTranslate } from "@tolgee/react";
import {
  blobToFile,
  getImageDimensions,
  getVideoDimensions,
  scaleDownImage,
} from "../../utils/helpers";
import { useDispatch, useSelector } from "react-redux";
import { lomavisCreatorRootState } from "../../../../pages/lomavisCreator";
import {
  clearEditedMediafile,
  setMediaDataAttribute,
  updateEditorCloseState,
  updateEditorUpdateState,
  updateMediaAssetFile,
  updateMultiImageEditorCloseState,
} from "../../../../redux/slices/lomavisCreator/lomavisCreator";
import axios from "axios";
import axiosClient from "../../../../utils/axios";
import toast from "react-hot-toast";
import { ALL, UPLOAD_TYPE_POST_MEDIA } from "../../../../config/constants";
import { resolve } from "../../../../api/endPoints";

// Load the Trim plugin view
setPlugins(plugin_trim);

const getLocale = (lang) => {
  switch (lang) {
    case "de":
    case "de-DE":
      return {
        ...EditorImageConfig.DeLocale,
      };
    case "en":
    case "en-GB":
    default:
      return {
        ...EditorImageConfig.EnLocale,
      };
  }
};

const imageEditorDefaults = getEditorDefaults({
  utils: EditorImageConfig.ImageEditorDefaults.utils,
  locale: getLocale(getLanguage()),

  imageReader: createDefaultImageReader(),
  imageWriter: createDefaultImageWriter({
    mimeType: "image/png",
    quality: 1.0,

    targetSize: {
      width: 1920,
      height: 1920,

      upscale: false,
    },
  }),

  shapePreprocessor: createDefaultShapePreprocessor(),
});

const stringifyImageState = (imageState) => {
  return JSON.stringify(imageState, (k, v) => (v === undefined ? null : v));
};

export default function VideoEditor({
  selectedFile,
  platform,
  startSubmission,
  footerRef,
  rightMargin,
  setStartSubmission,
  EditorArrayInfo,
  handleClose,
  fileInputRef,
  successNotification,
  handleCloseNoConfirmation,
  setCurrentIndex,
  currentIndex,
  totalItemsInUpload,
  scrollToTop,
}) {
  // inline result
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const [processingVideo, setProcessingVideo] = useState(false);
  const [result, setResult] = useState("");
  const [progress, setProgress] = useState(0);
  const [currentImage, setCurrentImage] = useState("");
  const [currentView, setCurrentView] = useState(EDITOR_VIEW);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [storedVideoState, setStoredVideoState] = useState(
    '{"cropLimitToImage": false }'
  );
  const [editorUpdateCount, setEditorUpdateCount] = useState(0);
  const [editorChanged, setEditorChanged] = useState(false);
  const [initializing, setInitializing] = useState(false);

  const [overlayImageURL, setOverlayImageURL] = useState(null);
  const [overlayImageState, setOverlayImageState] = useState(null);
  const [overlayImageHash, setOverlayImageHash] = useState("");
  const [overlayImage, setOverlayImage] = useState(null);
  const overlayEditorRef = useRef(null);

  const location_uuid = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.locationUuid
  );
  const cloud_uuid = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.cloudUuid
  );

  const editorRef = useRef(null);

  const createUploadWriter = () => [
    [
      // task
      async (state, options, onprogress) => {
        const editedImage = state.src; // This will be a blob

        const file_type = "video/mp4";
        let fixedBlob = editedImage;

        const url = URL.createObjectURL(state?.src);
        const preview_image = await getVideoThumbnail(state?.src);

        const dimensions = await getVideoDimensions(state?.src);

        setCurrentImage(url);
        setProcessingVideo(false);
        setStartSubmission(true);
        // Do something with the URL, e.g., display it, download it, etc.

        const uploadPreset = await generateFirebaseUploadUrl(
          {
            content_type: file_type,
            filesize_in_bytes: state?.src?.size,
            filename: selectedFile.name,
            uuid: selectedFile.source_media_uuid ? selectedFile.uuid : null,
            location_uuid: location_uuid ? location_uuid : null,
            cloud_uuid: cloud_uuid ? cloud_uuid : null,
            width: dimensions?.width,
            height: dimensions?.height,
            upload_type: UPLOAD_TYPE_POST_MEDIA,
            source_media_uuid: selectedFile.source_media_uuid
              ? selectedFile.source_media_uuid
              : selectedFile.uuid,
          },
          {},
          () => console.log("error here")
        );

        await uploadEditedVideo(
          state.imageState,
          overlayImageHash,
          selectedFile.uploadPreset?.uploaded_object_data?.path,
          uploadPreset?.uploaded_object_data?.path,
          uploadPreset,
          onprogress
        )
          .then((responseData) => {
            console.log("Upload successful:", responseData);
            EditorArrayInfo?.map((editor) => {
              updateMediaAssetFile({
                uuid: editor.videoInfo?.uploadPreset?.uuid,
                preview_image: preview_image,
                file: fixedBlob,
                assetBlob: url,
                width: dimensions?.width,
                height: dimensions?.height,
                platform: editor.platform,
                uploadPresets: uploadPreset,
                source_media_uuid: null,
              });
            });

            dispatch(
              updateMediaAssetFile({
                uuid: selectedFile?.uuid,
                preview_image: preview_image,
                file: fixedBlob,
                assetBlob: url,
                width: dimensions?.width,
                height: dimensions?.height,
                platform: platform,
                uploadPresets: uploadPreset,
                source_media_uuid: selectedFile.source_media_uuid
                  ? selectedFile.source_media_uuid
                  : selectedFile.uuid,
              })
            );
            return {
              ...state,
            };
          })
          .catch((error) => {
            console.error("Upload failed:", error);
            return error;
          });

        // upload instructions to your server here
        // - state.src => src file
        // - state.imageState => current state
      },

      // name of task
      "upload",
    ],
  ];

  const editorDefaults = getEditorDefaults({
    utils: EditorImageConfig.videoEditorDefaults.utils,
    locale: getLocale(getLanguage()),

    imageWriter: overlayImageURL
      ? createUploadWriter
      : EditorImageConfig.videoEditorDefaults.imageWriter,
  });

  const handleEditorInit = (editor) => {
    setInitializing(false);
    editorRef.current = editor;
  };
  const handleImageEditorInit = (editor) => {
    overlayEditorRef.current = editor;
  };
  const handleEditorReady = () => {
    setInitializing(true);
  };

  const handleInputChange = (e) => {
    // Exit if no files selected
    const fileList = e.target.files; // This is a FileList
    const fileArray = Array.from(fileList); // Convert the FileList to an array

    fileArray.map(async (file: any) => {
      let preview_image: any = URL.createObjectURL(file);
      let assetBlob: any = URL.createObjectURL(file);

      if (file) {
        try {
          const resizedBlob = await scaleDownImage(file);
          const url = URL.createObjectURL(resizedBlob);
          setUploadedImage(url);
        } catch (error) {
          console.error(error);
          setUploadedImage(preview_image);
        }
      }
    });

    // Edit the selected file
    fileInputRef.current.value = "";
  };

  const handleEditorUpdate = (imageState) => {
    setEditorUpdateCount((prevState) => prevState + 1);

    const imageStateStr = stringifyImageState(imageState);
    setStoredVideoState(imageStateStr);
  };

  const handleOverlayImageProcess = async () => {
    const imageSrc = overlayImageURL;
    const imageState = overlayImageState;
    try {
      overlayEditorRef.current.history.write(imageState);

      const editorHistory =
        overlayEditorRef.current.history.get()[
          overlayEditorRef.current.history.get().length - 1
        ];

      // Wait for user to process the image
      const result = await overlayEditorRef.current.processImage();

      // Get the result (blob or base64 URL depending on your needs)
      const { dest } = result;
      setOverlayImage(dest);
      const md5Hash = await generateMD5Hash(dest);

      setOverlayImageHash(md5Hash);
    } catch (error) {
      console.error("Error processing image:", error);
    }
  };

  const startExportImage = async () => {
    setProcessingVideo(true);
    if (editorRef.current) {
      const editor = editorRef.current;

      const editorHistory =
        editor.history.get()[editor.history.get().length - 1];

      if (
        [
          "annotation",
          "decoration",
          "manipulation",
          "redaction",
          "selection",
        ].every((key) => editorHistory?.[key]?.length === 0) &&
        !editorHistory?.colorMatrix &&
        !editorHistory?.frame &&
        !editorHistory?.trim
      ) {
        successNotification();

        dispatch(
          setMediaDataAttribute({
            platform: platform,
            uuid: selectedFile?.uuid,
            attribute: "newUpload",
            value: false,
          })
        );

        dispatch(
          updateMultiImageEditorCloseState({
            state: false,
          })
        );

        setEditorChanged(false);
        setProgress(0);
        setStartSubmission(false);
        setProcessingVideo(false);
        setCurrentView(UPLOAD_VIEW);
        scrollToTop();
        if (currentIndex + 1 >= totalItemsInUpload) {
          dispatch(
            clearEditedMediafile({
              platform: ALL,
            })
          );
          handleCloseNoConfirmation();
        } else {
          setCurrentIndex(currentIndex + 1);
        }

        editorRef.current.history.write([]);
        editorRef.current.value = "";

        setCurrentView(EDITOR_VIEW);

        setOverlayImage(null);
        setOverlayImageURL(null);
        setOverlayImageHash(null);

        return;
      }

      editor.processImage().then(async (result) => {
        // Handle the resulting image data (base64 or blob)

        const bgTransparentImage = await generateTransparentPNG(
          result.imageState.crop.width,
          result.imageState.crop.height
        );
        setOverlayImageURL(bgTransparentImage);
        setOverlayImageState(result.imageState);
      });
    }
  };

  const exportImage = async () => {
    if (editorRef.current) {
      const editor = editorRef.current;
      // Get the edited image

      editor
        .processImage()
        .then(async (result) => {
          // Handle the resulting image data (base64 or blob)

          setStartSubmission(false);

          setEditorUpdateCount(0);
          dispatch(
            updateMultiImageEditorCloseState({
              state: false,
            })
          );
          scrollToTop();
          if (currentIndex + 1 >= totalItemsInUpload) {
            handleCloseNoConfirmation();
            dispatch(
              clearEditedMediafile({
                platform: ALL,
              })
            );
          } else {
            setCurrentIndex(currentIndex + 1);
          }

          setCurrentView(EDITOR_VIEW);

          setEditorChanged(false);
          successNotification();
          setCurrentView(EDITOR_VIEW);
          setProgress(0);
          setOverlayImage(null);
          setOverlayImageURL(null);
          setOverlayImageHash(null);
        })
        .catch((error) => {
          setStartSubmission(false);
          console.error("Upload failed:", error);
          setProgress(0);
          setCurrentView(EDITOR_VIEW);
          setOverlayImage(null);
          setOverlayImageURL(null);
          setOverlayImageHash(null);
        });
    }
  };
  useEffect(() => {
    if (overlayImageHash && overlayImage) {
      exportImage();
    }
  }, [overlayImageHash, overlayImage]);

  const uploadEditedVideo = async (
    imageState,
    overlay_md5,
    input_file_path,
    output_file_path,
    uploadPreset,
    onprogress
  ) => {
    const formData = new FormData();

    // Append form fields
    formData.append("pintura_edit_history", JSON.stringify(imageState));
    formData.append("overlay_md5", overlay_md5);
    formData.append("input_file", input_file_path);
    formData.append("output_file", output_file_path);
    const url = resolve("generate_video_edit_url", {}, {});

    try {
      // Await the first POST request
      const response = await axiosClient.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      console.log("Response:", response.data);

      const { url: uploadUrl, payload } = response.data;
      const { input_file, output_file, overlay_md5, pintura_edit_history } =
        payload;

      const formData2 = new FormData();

      // Append the image file and other fields to the new FormData
      formData2.append(
        "overlay_image",
        blobToFile(overlayImage, "overlay_image")
      ); // 'overlay_image' is the expected key
      formData2.append("input_file", input_file);
      formData2.append("output_file", output_file);
      formData2.append("overlay_md5", overlay_md5);
      formData2.append("imageState", JSON.stringify(pintura_edit_history));

      try {
        // Await the second POST request
        const uploadResponse = await axios.post(uploadUrl, formData2, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onprogress(percentCompleted);
            setProgress(percentCompleted);
          },
        });
        console.log("Upload successful:", uploadResponse.data);

        try {
          // Await the PUT request to confirm the upload
          const confirmRes = await axiosClient.put(uploadPreset?.confirm_url, {
            success: true,
          });
          console.log("Upload confirmation:", confirmRes.data);

          return confirmRes.data; // Make sure this is returned current confirmation
        } catch (confirmErr) {
          console.error("Error confirming upload:", confirmErr);
          throw confirmErr; // Propagate error if needed
        }
      } catch (uploadErr) {
        console.error("Error uploading image:", uploadErr);
        throw uploadErr; // Propagate error if needed
      }
    } catch (initialErr) {
      console.error("Error during initial request:", initialErr);
      toast.error(` ${t("errors.request_couldnt_be_completed")}`, {
        duration: 4000,
        position: "top-right",
      });
      throw initialErr; // Handle and propagate initial POST error
    }
  };

  const putWithFile = async (url, file, headers, file_type, uploadPreset) => {
    try {
      const config = {
        headers: headers ? { ...headers, "Content-Type": file_type } : {}, // Set headers property with provided headers
      };
      const response = await axios.put(url, file, {
        ...config,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentCompleted);
        },
      });

      console.log("return data", response);

      try {
        const res = await axiosClient.put(uploadPreset?.confirm_url, {
          success: true,
        });
        console.log("upload confirmation", res.data);

        return res.data;
      } catch (err: any) {
        console.log(err);
      }
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request canceled:", error.message);
      } else {
        const res = error.response;

        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        console.error(`Looks like there was a problem. Status Code: ${error}`);

        console.error("Error:", error);
        throw error;
      }
      toast.error(` ${t("errors.request_couldnt_be_completed")}`, {
        duration: 4000,
        position: "top-right",
      });

      // Dispatch deleteAfile action inside the catch block
    }
  };
  const handleEditorProcess = (res) => {
    // 1. Turn imageState into string, replaces undefined values with null
    const imageStateStr = stringifyImageState(res.imageState);
    setStoredVideoState(imageStateStr);
  };
  const willRenderToolbar = (toolbar) => {
    // get the export button
    const exportButton = findNode("export", toolbar);

    removeNode("export", toolbar);

    return toolbar;
  };

  const handleImageUpdate = async () => {
    if (!uploadedImage || !storedVideoState) {
      return;
    }

    function removeObjectsByKeys(object, keysToRemove) {
      return Object.keys(object).reduce((result, key) => {
        if (!keysToRemove.includes(key)) {
          result[key] = object[key];
        }
        return result;
      }, {});
    }
    const dimensions = await getImageDimensions(uploadedImage);

    const currentState = JSON.parse(storedVideoState);
    const justAnnotations = [...currentState?.annotation];

    const filteredState = removeObjectsByKeys(currentState, ["annotation"]);
    const newAnnote = {
      aspectRatio: 1,
      backgroundColor: [0, 0, 0, 0],
      backgroundImage: uploadedImage,
      backgroundSize: "contain",
      cornerRadius: 0,
      disableErase: true,
      disableStyle: [
        "cornerRadius",
        "backgroundColor",
        "strokeColor",
        "strokeWidth",
      ],
      height: dimensions?.height,
      id: uploadedImage,
      isEditing: false,
      isSelected: true,
      opacity: 1,
      rotation: 0,
      strokeColor: [0, 0, 0],
      strokeWidth: 0,
      width: dimensions?.width,
      x: 0,
      y: 0,
    };
    const combined = {
      ...filteredState,
      annotation: [...justAnnotations, newAnnote],
    };

    const imageStateStr = stringifyImageState(combined);
    editorRef.current.history.write(JSON.parse(imageStateStr));
    editorRef.current.util = undefined;
    editorRef.current.util = "sticker";
  };

  useEffect(() => {
    handleImageUpdate();
  }, [uploadedImage]);
  const [componentKey, setComponentKey] = useState(0);
  useEffect(() => {
    setEditorUpdateCount(0);
    setComponentKey((prevKey) => prevKey + 1);
    handleEditorInit;
  }, [selectedFile]);

  useEffect(() => {
    setInitializing(false);
  }, [currentIndex]);

  return (
    <div className="d-flex w-100 h-100 flex-column gap-4">
      <div
        key={`${platform}-${componentKey}`}
        className="d-flex w-100 h-100 gap-4"
      >
        {selectedFile && (
          <div
            // style={{ minHeight: "500px" }}
            className="w-100 h-100 flex-fill"
          >
            <div className="w-100 h-100 position-relative d-flex flex-column gap-4 ">
              {processingVideo && (
                <div
                  style={{ zIndex: 10, top: "0px", left: "Opx" }}
                  className="position-absolute w-100 h-100 bg-white bg-opacity-25 rounded-3"
                ></div>
              )}
              {!editorChanged && (
                <div
                  onClick={() => {
                    dispatch(
                      updateMultiImageEditorCloseState({
                        state: true,
                      })
                    );
                    setEditorChanged(true);
                  }}
                  style={{ zIndex: 11, top: "0px", left: "Opx" }}
                  className="position-absolute w-100 h-100 bg-opacity-0% rounded-3"
                ></div>
              )}

              {currentView === EDITOR_VIEW && (
                <div className="w-100 bg-white p-3 h-100 rounded-3">
                  <PinturaEditor
                    {...editorDefaults}
                    ref={editorRef}
                    stickers={[
                      ["Emoji", [...EditorImageConfig?.Stickers]],
                      ["Markers", []],
                    ]}
                    src={
                      selectedFile?.assetBlob
                        ? selectedFile?.assetBlob
                        : selectedFile.original_asset
                    }
                    enableZoom={false}
                    enableZoomControls={false}
                    onLoad={(res) => console.log("load image", res)}
                    onInit={handleEditorInit}
                    onReady={handleEditorReady}
                    cropInteractionFocus={"selection"}
                    willRenderToolbar={willRenderToolbar}
                    stickerStickToImage={true}
                    onUpdate={handleEditorUpdate}
                    onProcess={handleEditorProcess}
                  />
                </div>
              )}

              {currentView === UPLOAD_VIEW && (
                <div className="w-100 bg-white p-3 h-100 rounded-3">
                  <div
                    style={{
                      width: "80%",
                      height: "90%",
                    }}
                    className="mx-auto align-items-center justify-content-center d-flex flex-column gap-4"
                  >
                    <div className="w-100 h-100 position-relative">
                      <video
                        style={{
                          maxHeight: "100%",
                          maxWidth: "100%",
                        }}
                        className="position-absolute top-50 start-50 translate-middle object-fit-contain"
                        src={currentImage}
                      />
                    </div>
                    <div style={{ height: "14px" }} className="w-50">
                      {progress > 0 && (
                        <ProgressBar
                          className="h-100"
                          variant="primary"
                          animated
                          now={progress}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {overlayImageURL && (
        <div
          style={{ visibility: "hidden", position: "absolute" }}
          className=" w-100 h-100 gap-4"
        >
          <h3>Processed Image</h3>
          <div className="w-100 bg-white p-3 h-100 rounded-3">
            <PinturaEditor
              {...imageEditorDefaults}
              ref={overlayEditorRef}
              src={overlayImageURL}
              onInit={handleImageEditorInit}
              onReady={handleOverlayImageProcess}
              cropInteractionFocus={"selection"}
              onUpdate={handleEditorUpdate}
              onProcess={handleEditorProcess}
            ></PinturaEditor>
          </div>
        </div>
      )}

      <div
        ref={footerRef}
        style={{ bottom: "0", zIndex: 100 }}
        className="w-100 d-flex justify-content-between gap-3 rounded-3 align-items-center bg-white py-2 px-3 border-top h-60px bg-white"
      >
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleInputChange}
            max={1}
            style={{ display: "none" }}
          />
        </div>
        <div
          style={{ marginRight: rightMargin + "px" }}
          className="d-flex justify-content-end gap-3 align-items-center py-2 flax-fill"
        >
          {!startSubmission && !processingVideo && (
            <Button
              onClick={() => {
                currentView === UPLOAD_VIEW
                  ? setCurrentView(EDITOR_VIEW)
                  : handleClose();
              }}
              variant="secondary"
              size="sm"
              disabled={startSubmission || processingVideo}
            >
              {t("lomavisCreator.back")}
            </Button>
          )}

          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              startExportImage();
            }}
            disabled={startSubmission || processingVideo || !initializing}
          >
            {(startSubmission || processingVideo) && (
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
            )}
            {startSubmission || processingVideo
              ? t("lomavisCreator.buttons.saving")
              : t("lomavisCreator.buttons.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
